// Saved for the report (Marius's reporting board 3, 1 Nov 2026): quotes,
// uptake, mission notes, lessons, decisions, risks, photos and news the team
// saves during the year, so the report is not written from memory. Saved with
// one button where the moment happens (a feedback answer, a passage of a
// mission report, a decision in minutes), by itself when an assumption
// changes state on Results, or with "+ Save something" (a link or a file).
// The report builder offers them as live cards and marks them used.
//
// API: /api/p/:projectId/admin/reports/saved (GET list, POST), /saved/options,
// /saved/:id (PATCH, DELETE), /saved/:id/use (PUT, DELETE).
import { z } from "zod";
import { uuid } from "../schemas/common";
import { isoDate, textOrEmpty } from "./common";

export const SAVED_KINDS = ["quote", "uptake", "mission_note", "lesson", "decision", "risk", "photo", "news"] as const;
export type SavedKind = (typeof SAVED_KINDS)[number];
export const SAVED_KIND_LABELS: Record<SavedKind, string> = {
  quote: "Quote",
  uptake: "Uptake",
  mission_note: "Mission note",
  lesson: "Lesson",
  decision: "Decision",
  risk: "Risk",
  photo: "Photo",
  news: "News",
};

/** Where a moment was saved. */
export const SAVED_SOURCES = ["feedback", "mission_report", "minutes", "assumption", "link", "file", "note"] as const;
export type SavedSource = (typeof SAVED_SOURCES)[number];

/**
 * not_needed: anonymous (a feedback answer, names removed) or nobody to ask;
 * asked: someone is quoted or shown and their agreement is still to be recorded;
 * given: their agreement is recorded.
 */
export const SAVED_CONSENTS = ["not_needed", "asked", "given"] as const;
export type SavedConsent = (typeof SAVED_CONSENTS)[number];

export type SavedItem = {
  id: string;
  kind: SavedKind;
  text: string;
  source: SavedSource;
  /** "Feedback form · A2.3 Durrës", "Mission report A3.1", "Minutes SC 2", "Assumption at risk · Results". Never a respondent's name. */
  sourceLabel: string;
  /** Where it was saved, in the app (or the outside address of a link); null when there is no page for it. */
  href: string | null;
  activityId: string | null;
  activityCode: string | null;
  resultId: string | null;
  /** "R2"; null when not tagged to a result. */
  resultCode: string | null;
  /** The day the moment happened (YYYY-MM-DD): the period filter uses it. */
  momentDate: string;
  /** Who saved it; null when it was saved by itself (an assumption that changed). */
  savedByName: string | null;
  fileId: string | null;
  fileName: string | null;
  /** The file's download address, when there is a file. */
  fileUrl: string | null;
  url: string | null;
  consent: SavedConsent;
  /** Where the report builder used it; null = not used yet. */
  usedIn: { reportId: string; reportTitle: string; sectionKey: string; sectionLabel: string } | null;
  createdAt: string;
};

/** A reporting period from the calendar, to pick which report the list is for. */
export type SavedPeriod = { id: string; label: string; from: string; to: string; reportId: string | null };

export type SavedList = {
  items: SavedItem[];
  /** Counted over the period, before the kind and "not used yet" filters (for the filter pills). */
  counts: { all: number; unused: number; byKind: Record<SavedKind, number> };
  /** The reporting calendar, oldest first. */
  periods: SavedPeriod[];
  /** The period the list is limited to; null = the whole project. */
  period: SavedPeriod | null;
  /** The period of the report being written (due first), else the one around today: the page opens on it. */
  suggestedPeriodId: string | null;
};

/** The pickers of the save dialog: results to tag and activities. */
export type SavedOptions = {
  results: { id: string; code: string; label: string }[];
  activities: { id: string; code: string; title: string }[];
};

/** GET …/admin/reports/saved: every filter optional; reportId wins over period, period over from/to. */
export const savedListQuery = z.object({
  reportId: uuid.optional(),
  /** A reporting period id, "all" (the whole project) or "suggested" (suggestedPeriodId, else the whole project). */
  period: z.union([uuid, z.literal("all"), z.literal("suggested")]).optional(),
  kind: z.enum(SAVED_KINDS).optional(),
  activityId: uuid.optional(),
  resultId: uuid.optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  unused: z.enum(["1", "0"]).optional(),
});
export type SavedListQuery = z.infer<typeof savedListQuery>;

/**
 * POST …/admin/reports/saved. With source feedback, sourceId is the answer's id
 * (FeedbackResults.textAnswers[].id): the server takes the text from the answer
 * itself, with names removed. mission_report: sourceId = the mission (activity);
 * minutes: sourceId = the meeting; text = the passage or decision.
 */
export const newSavedBody = z
  .object({
    kind: z.enum(SAVED_KINDS),
    source: z.enum(["feedback", "mission_report", "minutes", "link", "file", "note"]),
    sourceId: uuid.nullable().default(null),
    text: textOrEmpty(2000),
    resultId: uuid.nullable().default(null),
    activityId: uuid.nullable().default(null),
    momentDate: isoDate.nullable().default(null),
    url: z.string().trim().url("Enter the whole address, starting with https://").max(1000).nullable().default(null),
    fileId: uuid.nullable().default(null),
    /** "They agreed to be quoted or shown". */
    consentGiven: z.boolean().default(false),
  })
  .superRefine((b, ctx) => {
    if (b.source !== "feedback" && !b.text.trim()) ctx.addIssue({ code: "custom", path: ["text"], message: "Write what to keep for the report." });
    if (["feedback", "mission_report", "minutes"].includes(b.source) && !b.sourceId) ctx.addIssue({ code: "custom", path: ["sourceId"], message: "Say where it comes from." });
    if (b.source === "link" && !b.url) ctx.addIssue({ code: "custom", path: ["url"], message: "Enter the link." });
    if (b.source === "file" && !b.fileId) ctx.addIssue({ code: "custom", path: ["fileId"], message: "Add the file." });
  });
export type NewSavedBody = z.input<typeof newSavedBody>;

export const savedPatch = z
  .object({
    kind: z.enum(SAVED_KINDS),
    text: z.string().trim().min(1, "Write what to keep for the report.").max(2000),
    resultId: uuid.nullable(),
    activityId: uuid.nullable(),
    momentDate: isoDate,
    consent: z.enum(SAVED_CONSENTS),
  })
  .partial()
  .strict();
export type SavedPatch = z.infer<typeof savedPatch>;

/** PUT …/saved/:id/use: the report builder inserted it in a section of a report. */
export const savedUseBody = z
  .object({
    reportId: uuid,
    sectionKey: z.string().trim().min(1).max(40),
    sectionLabel: z.string().trim().min(1).max(120),
  })
  .strict();
export type SavedUseBody = z.infer<typeof savedUseBody>;
